// UnitsSummaryTable — per-unit fee rollup. Sprint 5d extraction.
//
// Stateless. Receives the rows + the "open drill-down" callback. The
// status-pill logic (overdue / pending / all-paid / no-fees) is inlined
// here because it's pure presentation; if a second consumer ever needs
// it, lift to feeRules.
import React from 'react';
import { Badge, Button, Space, Table } from 'antd';
import { EyeOutlined, HomeOutlined } from '@ant-design/icons';
import type { UnitFeeSummary } from './feeRules';
import { formatCurrency } from './feeRules';

export interface UnitsSummaryTableProps {
  rows: UnitFeeSummary[];
  onView: (row: UnitFeeSummary) => void;
}

export const UnitsSummaryTable: React.FC<UnitsSummaryTableProps> = ({ rows, onView }) => {
  const columns = [
    {
      title: 'Unit',
      dataIndex: 'unitNumber',
      key: 'unitNumber',
      render: (text: string) => (
        <Space>
          <HomeOutlined />
          <span className="font-medium">{text}</span>
        </Space>
      ),
    },
    { title: 'Owner', dataIndex: 'ownerName', key: 'ownerName' },
    {
      title: 'Total Fees',
      dataIndex: 'totalFees',
      key: 'totalFees',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: 'Paid',
      key: 'paid',
      render: (_: unknown, r: UnitFeeSummary) => (
        <span className="text-green-600">{formatCurrency(r.paidFees)}</span>
      ),
    },
    {
      title: 'Pending',
      key: 'pending',
      render: (_: unknown, r: UnitFeeSummary) => (
        <span className="text-orange-600">{formatCurrency(r.pendingFees)}</span>
      ),
    },
    {
      title: 'Overdue',
      key: 'overdue',
      render: (_: unknown, r: UnitFeeSummary) => (
        <span className="text-red-600">{formatCurrency(r.overdueFees)}</span>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, r: UnitFeeSummary) => {
        if (r.overdueFees > 0) return <Badge status="error" text="Has Overdue" />;
        if (r.pendingFees > 0) return <Badge status="warning" text="Pending Payment" />;
        if (r.totalFees > 0 && r.paidFees === r.totalFees) return <Badge status="success" text="All Paid" />;
        return <Badge status="default" text="No Fees" />;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, r: UnitFeeSummary) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => onView(r)}>
          View Details
        </Button>
      ),
    },
  ];

  // Sprint 139 — keyed on `rowKey`, not `ownerId`: the latter is the
  // fee-matching key and is not guaranteed unique across owner rows.
  return (
    <Table
      columns={columns}
      dataSource={rows}
      rowKey="rowKey"
      pagination={{ pageSize: 10 }}
      scroll={{ x: 800 }}
    />
  );
};
